import { useCallback, useRef } from 'react';

interface IAnimatedRowItem {
  id: string;
  isNew?: boolean;
}

interface IUseAnimatedRowPropsOptions {
  /** CSS class name to apply to new rows for animation */
  animationClassName: string;
}

interface IRowInfo<T extends IAnimatedRowItem> {
  original: T;
}

type TGetTrProps<T extends IAnimatedRowItem> = (
  state: unknown,
  rowInfo?: IRowInfo<T>,
) => Record<string, unknown>;

interface IUseAnimatedRowPropsReturn<T extends IAnimatedRowItem> {
  /** Function to pass to Table's getTrProps prop */
  getTrProps: TGetTrProps<T>;
  /** Reset the tracked animated row IDs (useful when data is cleared) */
  resetAnimatedIds: () => void;
}

/**
 * Hook for managing row animation state in tables with realtime data.
 * Tracks which rows have been animated to prevent re-animation on re-renders.
 *
 * @param options - Configuration options
 * @returns Object with getTrProps function and reset utility
 *
 * @example
 * ```tsx
 * function MyTable() {
 *   const { getTrProps } = useAnimatedRowProps<ITransaction>({
 *     animationClassName: styles.animateIn,
 *   });
 *
 *   return (
 *     <Table
 *       animateRows={true}
 *       getTrProps={getTrProps}
 *       items={transactions}
 *       // ...
 *     />
 *   );
 * }
 * ```
 */
function useAnimatedRowProps<T extends IAnimatedRowItem>(
  options: IUseAnimatedRowPropsOptions,
): IUseAnimatedRowPropsReturn<T> {
  const { animationClassName } = options;
  const animatedRowIds = useRef(new Set<string>());

  const getTrProps: TGetTrProps<T> = useCallback(
    (_state: unknown, rowInfo?: IRowInfo<T>): Record<string, unknown> => {
      const props: Record<string, unknown> = {};

      if (!rowInfo?.original) {
        return props;
      }

      const { id, isNew } = rowInfo.original;

      // Pass the row ID for animation key tracking
      if (id) {
        props['data-row-key'] = id;
      }

      // Add animation class for new rows
      if (isNew) {
        const shouldAnimate = !animatedRowIds.current.has(id);

        // Always track the row to prevent re-animation on subsequent renders
        animatedRowIds.current.add(id);

        if (shouldAnimate) {
          props.className = animationClassName;
        }
      }

      return props;
    },
    [animationClassName],
  );

  const resetAnimatedIds = useCallback((): void => {
    animatedRowIds.current.clear();
  }, []);

  return {
    getTrProps,
    resetAnimatedIds,
  };
}

export default useAnimatedRowProps;
export type {
  IAnimatedRowItem,
  IUseAnimatedRowPropsOptions,
  IUseAnimatedRowPropsReturn,
  IRowInfo,
  TGetTrProps,
};
